import React, { useEffect } from 'react';
import queryString from 'query-string';
import { useLocation } from 'react-router';
import { Route, Switch, Redirect } from 'react-router-dom';
import ReactGA from 'react-ga';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { internalRedirect } from 'helpers';

import Home from 'products/global/views/Home';
import Contact from 'products/global/views/Contact';
import Team from 'products/global/views/Team';
import JoinUs from 'products/global/views/JoinUs';
import AmbassadorRoutes from 'products/global/views/Ambassador';
import ForgotPassword from 'products/global/views/ForgotPassword';
import NotFoundPage from 'products/global/views/NotFoundPage';
import PrivacyPolicy from 'products/global/views/PrivacyPolicy';
import Policies from 'products/global/views/Policies';
import Partners from 'products/global/views/Partners';
import Profile from 'products/global/views/Profile';
import ChooseProduct from 'products/global/views/ChooseProduct';
import Ukraine from 'products/global/views/Ukraine';
import DownloadApp from 'products/global/views/DownloadApp';

import NomadInsuranceRoutes from 'products/nomad_insurance/routing';

import { GOOGLE_ANALYTICS_APP_ID } from 'api/ga';
import useIntercom from 'hooks/useIntercom';

import PrivateRoute from './private-route';
import ProtectedRoute from './protected-route';
import history from './history';

function applyReferenceID(id) {
  const year = 365 * 24 * 60 * 60 * 1000;
  localStorage.setItem('referenceID', id);
  localStorage.setItem('referenceIDExpiration', Date.now() + year);
}

const addUtmParameters = (referenceID, campaign) => {
  let params = `?referenceID=${referenceID}`;
  if (campaign) {
    params += `&campaign=${campaign}`;
  }
  params += `&utm_source=${referenceID}&utm_medium=Ambassador`;
  return params;
};

const Routing = () => {
  const location = useLocation();
  useIntercom();
  const { userID } = useSelector((state) => state.app.user);

  useEffect(() => {
    if (userID) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        userId: userID
      });
    }
  }, [userID]);

  useEffect(() => {
    ReactGA.initialize(GOOGLE_ANALYTICS_APP_ID);
    ReactGA.pageview(history.location.pathname + history.location.search);
    history.listen((loc) => {
      ReactGA.pageview(loc.pathname + loc.search);
    });
  }, []);

  const {
    referenceID,
    utm_source: utmSource,
    campaign
  } = queryString.parse(location.search);

  if (referenceID) {
    applyReferenceID(referenceID);

    if (campaign) {
      localStorage.setItem('campaign', campaign);
    }

    if (!utmSource) {
      history.push({
        pathname: location.pathname,
        search: addUtmParameters(referenceID, campaign)
      });
    }
  }

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [location.pathname]);

  // TODO: Remove this after the migration to safetywing-member is complete
  const isLegacyStaging = window.location.hostname.includes('legacy.test-bird.one');

  if (!isLegacyStaging) {
    return (
      <Switch>
        {/* Internal redirects to safetywing-member */}
        <Route exact path="/" render={() => internalRedirect('/')} />
        <Route exact path="/contact" render={() => internalRedirect('/contact')} />
        <Route exact path="/login" render={() => internalRedirect('/login')} />
        <Route exact path="/signup" render={() => internalRedirect('/signup')} />
        <Route exact path="/profile" render={() => internalRedirect('/dashboard')} />
        <Route
          exact
          path="/download-app"
          render={() => internalRedirect('/download-app')}
        />
        <Route
          exact
          path="/privacy-policy"
          render={() => internalRedirect('/privacy-policy')}
        />
        <Route
          exact
          path="/forgot-password"
          render={() => internalRedirect('/forgot-password')}
        />
        <Route
          exact
          path="/terms-and-conditions"
          render={() => internalRedirect('/terms-and-conditions')}
        />

        {/* Global routes */}
        <ProtectedRoute exact path="/team" product="global" component={Team} />
        <ProtectedRoute exact path="/join-us" product="global" component={JoinUs} />
        <ProtectedRoute exact path="/policies" product="global" component={Policies} />

        {/* Ambassador routes */}
        <ProtectedRoute
          path="/ambassador"
          component={AmbassadorRoutes}
          product="ambassador"
        />

        {/* Product routes */}
        <Route path="/nomad-insurance" component={NomadInsuranceRoutes} />
        <Route path="/nomads" component={NomadInsuranceRoutes} />
        <ProtectedRoute
          product="remote_health"
          path="/remote-health*"
          component={({ location }) => {
            if (process.env.NODE_ENV !== 'development') {
              Sentry.captureMessage(`Internal RH navigation: ${location?.pathname}`);
            }
            window.open(window.location.href, '_self').focus();
            return null;
          }}
        />
        <Route path="/*" component={NotFoundPage} />
      </Switch>
    );
  }

  return (
    <Switch>
      {/* Global routes */}
      <ProtectedRoute exact path="/" product="global" component={Home} />
      <ProtectedRoute exact path="/contact" product="global" component={Contact} />
      <ProtectedRoute exact path="/team" product="global" component={Team} />
      <ProtectedRoute exact path="/join-us" product="global" component={JoinUs} />
      <ProtectedRoute
        exact
        path="/download-app"
        product="global"
        component={DownloadApp}
      />
      <ProtectedRoute
        exact
        path="/forgot-password"
        product="global"
        component={ForgotPassword}
      />
      <ProtectedRoute
        exact
        path="/privacy-policy"
        product="global"
        component={PrivacyPolicy}
      />
      <ProtectedRoute exact path="/policies" product="global" component={Policies} />
      <ProtectedRoute exact path="/partners" product="global" component={Partners} />
      <ProtectedRoute exact path="/signup" product="global" component={ChooseProduct} />
      <PrivateRoute exact path="/profile" product="global" component={Profile} />
      <Route path="/ukraine" component={Ukraine} />
      <Route path="/ukraine-resources" component={Ukraine} />

      {/* Ambassador routes */}
      <ProtectedRoute
        path="/ambassador"
        component={AmbassadorRoutes}
        product="ambassador"
      />

      {/* Redirect for legacy routes */}
      <Redirect from="/(affiliate|ambassador)/remote" to="/ambassador" />
      <Redirect from="/user/affiliate-dashboard" to="/ambassador" />
      <Redirect from="/affiliate" to="/ambassador" />
      <Redirect from="/affiliate/signup" to="/ambassador/registration" />
      <Redirect from="/user/affiliate-signup" to="/ambassador/registration" />
      <Redirect
        from="/nomad-insurance/user/affiliate-signup"
        to="/ambassador/registration"
      />

      <Redirect from="/nomad-insurance/partners" to="/partners" />
      <Redirect from="/nomad-insurance/profile/insurance" to="/nomad-insurance/profile" />

      {/* Legacy affiliate link */}
      <Route
        exact
        path="/a/:id"
        render={({ match }) => {
          const newUrl = `/nomad-insurance${addUtmParameters(match.params.id, null)}`;
          return <Redirect to={newUrl} />;
        }}
      />

      {/* Product routes */}
      <Redirect exact path="/" to="/nomad-insurance" />
      <Route path="/nomad-insurance" component={NomadInsuranceRoutes} />
      <Route path="/nomads" component={NomadInsuranceRoutes} />
      <Route
        exact
        path="/rh-registration"
        component={() => {
          const win = window.open('https://hello.safetywing.com/lets-connect', '_self');
          win.focus();
          return null;
        }}
      />
      <ProtectedRoute
        product="remote_health"
        path="/remote-health*"
        component={({ location }) => {
          if (process.env.NODE_ENV !== 'development') {
            Sentry.captureMessage(`Internal RH navigation: ${location?.pathname}`);
          }
          window.open(window.location.href, '_self').focus();
          return null;
        }}
      />
      <Route path="/*" component={NotFoundPage} />
    </Switch>
  );
};

export default Routing;
